"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SettingsProvider = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_router_dom_1 = require("react-router-dom");
const constants_1 = require("@framework/constants");
const provider_user_1 = require("@framework/provider-user");
const constants_2 = require("./constants");
const context_1 = require("./context");
const interfaces_1 = require("./interfaces");
const utils_1 = require("./utils");
const SettingsProvider = (props) => {
    var _a, _b, _c, _d;
    const { defaultLanguage = constants_1.EnabledLanguages.RU, defaultLayoutDirection = interfaces_1.LAYOUT_DIRECTION.ltr, defaultSchemeState = constants_2.DEFAULT_SCHEME_STATE, defaultThemeType = constants_1.ThemeType.light, defaultReferrer = "0x0000000000000000000000000000000000000000", // do not import ethers!
    storageName = "settings", children, } = props;
    const user = (0, provider_user_1.useUser)();
    const location = (0, react_router_dom_1.useLocation)();
    const navigate = (0, react_router_dom_1.useNavigate)();
    const locationRef = (0, react_1.useRef)(location);
    locationRef.current = location;
    const [searchParams] = (0, react_router_dom_1.useSearchParams)();
    const [storedState, setStoredState] = (0, react_1.useState)({ key: "", settings: {} });
    const storedStateRef = (0, react_1.useRef)(storedState);
    storedStateRef.current = storedState;
    const { settings } = storedState;
    const generation = (_a = settings.resetGeneration) !== null && _a !== void 0 ? _a : 0;
    const defaultValues = {
        language: defaultLanguage,
        schemeState: defaultSchemeState,
        layoutDirection: defaultLayoutDirection,
        themeType: defaultThemeType,
        referrer: defaultReferrer,
        TCILevels: constants_2.DEFAULT_TCI_LEVELS,
        schemeColumnCount: constants_2.DEFAULT_SCHEME_COLUMN_COUNT,
        tab: "",
        stationId: "",
        trainId: "",
        startTimestamp: "",
        endTimestamp: "",
        rangeType: null,
        passwordExpiryLastShownAt: "",
    };
    const getUserStorageKey = (0, react_1.useCallback)(() => {
        var _a;
        if (user.isAuthenticated() && ((_a = user.profile) === null || _a === void 0 ? void 0 : _a.id)) {
            return `${storageName}_${user.profile.id}`;
        }
        return `${storageName}_guest`;
    }, [(_b = user.profile) === null || _b === void 0 ? void 0 : _b.id, user.isAuthenticated(), storageName]);
    const storageKey = getUserStorageKey();
    const serverRevision = (_c = user.profile) === null || _c === void 0 ? void 0 : _c.settingsResetRevision;
    const activeStorageRef = (0, react_1.useRef)({ key: storageKey, serverRevision });
    activeStorageRef.current = { key: storageKey, serverRevision };
    const isSynchronized = !user.isLoading &&
        storedState.key === storageKey &&
        (serverRevision === undefined || serverRevision <= ((_d = settings.settingsResetRevision) !== null && _d !== void 0 ? _d : 0));
    const clearTimeRange = (0, react_1.useCallback)(() => {
        const currentLocation = locationRef.current;
        const params = new URLSearchParams(currentLocation.search);
        if (!["rangeType", "startTimestamp", "endTimestamp"].some(key => params.has(key)))
            return;
        params.delete("rangeType");
        params.delete("startTimestamp");
        params.delete("endTimestamp");
        navigate({ pathname: currentLocation.pathname, search: params.toString(), hash: currentLocation.hash }, { replace: true, state: currentLocation.state });
    }, [navigate]);
    const createResetSettings = (0, react_1.useCallback)((current, revision) => {
        var _a, _b;
        return (Object.assign(Object.assign(Object.assign({}, current), { language: defaultLanguage, layoutDirection: defaultLayoutDirection, schemeState: defaultSchemeState, themeType: defaultThemeType, TCILevels: constants_2.DEFAULT_TCI_LEVELS, schemeColumnCount: constants_2.DEFAULT_SCHEME_COLUMN_COUNT, rangeType: null, startTimestamp: "", endTimestamp: "", resetGeneration: ((_a = current.resetGeneration) !== null && _a !== void 0 ? _a : 0) + 1 }), (revision !== undefined && { settingsResetRevision: Math.max(revision, (_b = current.settingsResetRevision) !== null && _b !== void 0 ? _b : 0) })));
    }, [defaultLanguage, defaultLayoutDirection, defaultSchemeState, defaultThemeType]);
    const publishSettings = (0, react_1.useCallback)((key, nextSettings) => {
        if (key !== activeStorageRef.current.key)
            return;
        const nextState = { key, settings: nextSettings };
        storedStateRef.current = nextState;
        setStoredState(nextState);
    }, []);
    const readSynchronizedSettings = (0, react_1.useCallback)(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        let current = (0, utils_1.readSettingsFromLocalStorage)(storageKey);
        const published = storedStateRef.current.key === storageKey ? storedStateRef.current.settings : undefined;
        const expectedRevision = Math.max((_a = activeStorageRef.current.serverRevision) !== null && _a !== void 0 ? _a : 0, (_b = published === null || published === void 0 ? void 0 : published.settingsResetRevision) !== null && _b !== void 0 ? _b : 0);
        const didReset = expectedRevision > ((_c = current.settingsResetRevision) !== null && _c !== void 0 ? _c : 0);
        if (didReset) {
            current = createResetSettings(Object.assign(Object.assign({}, current), { resetGeneration: Math.max((_d = current.resetGeneration) !== null && _d !== void 0 ? _d : 0, (_e = published === null || published === void 0 ? void 0 : published.resetGeneration) !== null && _e !== void 0 ? _e : 0) }), expectedRevision);
            (0, utils_1.saveSettingsToLocalStorage)(storageKey, current);
        }
        else if (published &&
            ((_f = current.settingsResetRevision) !== null && _f !== void 0 ? _f : 0) > ((_g = published.settingsResetRevision) !== null && _g !== void 0 ? _g : 0) &&
            ((_h = current.resetGeneration) !== null && _h !== void 0 ? _h : 0) <= ((_j = published.resetGeneration) !== null && _j !== void 0 ? _j : 0)) {
            current = Object.assign(Object.assign({}, current), { resetGeneration: ((_k = published.resetGeneration) !== null && _k !== void 0 ? _k : 0) + 1 });
            (0, utils_1.saveSettingsToLocalStorage)(storageKey, current);
        }
        else if (published && ((_l = current.resetGeneration) !== null && _l !== void 0 ? _l : 0) < ((_m = published.resetGeneration) !== null && _m !== void 0 ? _m : 0)) {
            current = published;
            (0, utils_1.saveSettingsToLocalStorage)(storageKey, current);
        }
        else if (activeStorageRef.current.serverRevision === 0 && current.settingsResetRevision === undefined) {
            current = Object.assign(Object.assign({}, current), { settingsResetRevision: 0 });
            (0, utils_1.saveSettingsToLocalStorage)(storageKey, current);
        }
        return { settings: current, didReset };
    }, [storageKey, createResetSettings]);
    const updateSettings = (0, react_1.useCallback)((updates) => {
        var _a, _b, _c;
        if (storageKey !== activeStorageRef.current.key)
            return;
        try {
            const { settings: current } = readSynchronizedSettings();
            if (((_a = current.resetGeneration) !== null && _a !== void 0 ? _a : 0) !== generation) {
                if (((_b = current.resetGeneration) !== null && _b !== void 0 ? _b : 0) !== ((_c = storedStateRef.current.settings.resetGeneration) !== null && _c !== void 0 ? _c : 0)) {
                    clearTimeRange();
                }
                publishSettings(storageKey, current);
                return;
            }
            const nextSettings = Object.assign(Object.assign({}, current), updates);
            (0, utils_1.saveSettingsToLocalStorage)(storageKey, nextSettings);
            publishSettings(storageKey, nextSettings);
        }
        catch (error) {
            console.error("Failed to update settings:", error);
        }
    }, [storageKey, generation, clearTimeRange, publishSettings, readSynchronizedSettings]);
    const resetSettings = (0, react_1.useCallback)((revision) => {
        var _a;
        if (storageKey !== activeStorageRef.current.key)
            return;
        const { settings: current, didReset } = readSynchronizedSettings();
        if (revision !== undefined && revision > 0 && revision <= ((_a = current.settingsResetRevision) !== null && _a !== void 0 ? _a : 0)) {
            if (didReset)
                clearTimeRange();
            publishSettings(storageKey, current);
            return;
        }
        const nextSettings = createResetSettings(current, revision);
        (0, utils_1.saveSettingsToLocalStorage)(storageKey, nextSettings);
        publishSettings(storageKey, nextSettings);
    }, [storageKey, createResetSettings, clearTimeRange, publishSettings, readSynchronizedSettings]);
    const isEmptyValue = (value) => {
        return value === undefined || value === "";
    };
    const getSchemeState = () => { var _a; return (_a = settings.schemeState) !== null && _a !== void 0 ? _a : defaultValues.schemeState; };
    const getLanguage = () => {
        var _a, _b, _c;
        return (_c = (_a = settings.language) !== null && _a !== void 0 ? _a : (user.isAuthenticated() && ((_b = user.profile) === null || _b === void 0 ? void 0 : _b.language) ? user.profile.language : null)) !== null && _c !== void 0 ? _c : defaultValues.language;
    };
    const getTCILevels = () => { var _a; return (_a = settings.TCILevels) !== null && _a !== void 0 ? _a : defaultValues.TCILevels; };
    const getLayoutDirection = () => { var _a; return (_a = settings.layoutDirection) !== null && _a !== void 0 ? _a : defaultValues.layoutDirection; };
    const getTheme = () => { var _a; return (_a = settings.themeType) !== null && _a !== void 0 ? _a : defaultValues.themeType; };
    const getReferrer = () => { var _a; return (_a = settings.referrer) !== null && _a !== void 0 ? _a : defaultValues.referrer; };
    const getTab = () => { var _a; return (_a = settings.tab) !== null && _a !== void 0 ? _a : defaultValues.tab; };
    const getStationId = () => { var _a; return (_a = settings.stationId) !== null && _a !== void 0 ? _a : defaultValues.stationId; };
    const getTrainId = () => { var _a; return (_a = settings.trainId) !== null && _a !== void 0 ? _a : defaultValues.trainId; };
    const getStartTimestamp = () => { var _a; return (_a = settings.startTimestamp) !== null && _a !== void 0 ? _a : defaultValues.startTimestamp; };
    const getEndTimestamp = () => { var _a; return (_a = settings.endTimestamp) !== null && _a !== void 0 ? _a : defaultValues.endTimestamp; };
    const getRangeType = () => { var _a; return (_a = settings.rangeType) !== null && _a !== void 0 ? _a : defaultValues.rangeType; };
    const getSchemeColumnCount = () => { var _a; return (_a = settings.schemeColumnCount) !== null && _a !== void 0 ? _a : defaultValues.schemeColumnCount; };
    const getPasswordExpireyLastShownAt = () => { var _a; return (_a = settings.passwordExpiryLastShownAt) !== null && _a !== void 0 ? _a : defaultValues.passwordExpiryLastShownAt; };
    const setSchemeState = (0, react_1.useCallback)((schemeState) => {
        updateSettings({ schemeState });
    }, [updateSettings]);
    const setLanguage = (0, react_1.useCallback)((language) => {
        updateSettings({ language });
    }, [updateSettings]);
    const setTCILevels = (0, react_1.useCallback)((TCILevels) => {
        updateSettings({ TCILevels });
    }, [updateSettings]);
    const setLayoutDirection = (0, react_1.useCallback)((layoutDirection) => {
        updateSettings({ layoutDirection });
    }, [updateSettings]);
    const setTheme = (0, react_1.useCallback)((themeType) => {
        updateSettings({ themeType });
    }, [updateSettings]);
    const setReferrer = (0, react_1.useCallback)((referrer) => {
        updateSettings({ referrer });
    }, [updateSettings]);
    const setTab = (0, react_1.useCallback)((tab) => {
        updateSettings({ tab });
    }, [updateSettings]);
    const setStationId = (0, react_1.useCallback)((stationId) => {
        updateSettings(Object.assign({ stationId }, (!isEmptyValue(stationId) && { trainId: "" })));
    }, [updateSettings, isEmptyValue]);
    const setTrainId = (0, react_1.useCallback)((trainId) => {
        updateSettings(Object.assign({ trainId }, (!isEmptyValue(trainId) && { stationId: "" })));
    }, [updateSettings, isEmptyValue]);
    const setSelectedItem = (0, react_1.useCallback)((stationId = "", trainId = "") => {
        updateSettings({ stationId, trainId });
    }, [updateSettings]);
    const setStartTimestamp = (0, react_1.useCallback)((startTimestamp) => {
        updateSettings({ startTimestamp });
    }, [updateSettings]);
    const setEndTimestamp = (0, react_1.useCallback)((endTimestamp) => {
        updateSettings({ endTimestamp });
    }, [updateSettings]);
    const setTimestamps = (0, react_1.useCallback)((startTimestamp, endTimestamp) => {
        updateSettings({ startTimestamp, endTimestamp });
    }, [updateSettings]);
    const setRangeType = (0, react_1.useCallback)((rangeType) => {
        updateSettings({ rangeType });
    }, [updateSettings]);
    const setSchemeColumnCount = (0, react_1.useCallback)((schemeColumnCount) => {
        updateSettings({ schemeColumnCount });
    }, [updateSettings]);
    const setPasswordExpiryLastShownAt = (0, react_1.useCallback)((passwordExpiryLastShownAt) => {
        updateSettings({ passwordExpiryLastShownAt });
    }, [updateSettings]);
    (0, react_1.useLayoutEffect)(() => {
        var _a;
        if (user.isLoading)
            return;
        const { settings: current, didReset } = readSynchronizedSettings();
        if (didReset || (storedState.key === storageKey && ((_a = current.resetGeneration) !== null && _a !== void 0 ? _a : 0) !== generation)) {
            clearTimeRange();
        }
        publishSettings(storageKey, current);
    }, [storageKey, serverRevision, user.isLoading, readSynchronizedSettings, publishSettings]);
    (0, react_1.useEffect)(() => {
        const handleStorage = (event) => {
            var _a, _b;
            if (storageKey !== activeStorageRef.current.key || event.key !== storageKey || event.storageArea !== localStorage)
                return;
            const { settings: current } = readSynchronizedSettings();
            if (((_a = current.resetGeneration) !== null && _a !== void 0 ? _a : 0) !== ((_b = storedStateRef.current.settings.resetGeneration) !== null && _b !== void 0 ? _b : 0)) {
                clearTimeRange();
            }
            publishSettings(storageKey, current);
        };
        window.addEventListener("storage", handleStorage);
        return () => window.removeEventListener("storage", handleStorage);
    }, [storageKey, generation, clearTimeRange, publishSettings, readSynchronizedSettings]);
    (0, react_1.useEffect)(() => {
        const referrer = searchParams.get("referrer");
        if (isSynchronized && referrer) {
            setReferrer(referrer);
        }
    }, [searchParams, isSynchronized]);
    (0, react_1.useEffect)(() => {
        if (!isSynchronized)
            return;
        if (settings.language === constants_1.RTLLanguages.AR) {
            setLayoutDirection(interfaces_1.LAYOUT_DIRECTION.rtl);
        }
        else {
            setLayoutDirection(interfaces_1.LAYOUT_DIRECTION.ltr);
        }
    }, [settings.language, isSynchronized]);
    const contextValue = (0, react_1.useMemo)(() => ({
        getLanguage,
        setLanguage,
        getLayoutDirection,
        setLayoutDirection,
        getSchemeState,
        setSchemeState,
        getTCILevels,
        setTCILevels,
        getTheme,
        setTheme,
        getTab,
        setTab,
        getStationId,
        setStationId,
        getTrainId,
        setTrainId,
        setSelectedItem,
        getStartTimestamp,
        setStartTimestamp,
        getEndTimestamp,
        setEndTimestamp,
        setTimestamps,
        getRangeType,
        setRangeType,
        getReferrer,
        setReferrer,
        getSchemeColumnCount,
        setSchemeColumnCount,
        getPasswordExpireyLastShownAt,
        setPasswordExpiryLastShownAt,
        resetSettings,
    }), [
        settings.language,
        settings.layoutDirection,
        settings.schemeState,
        settings.TCILevels,
        settings.themeType,
        settings.tab,
        settings.stationId,
        settings.trainId,
        settings.startTimestamp,
        settings.endTimestamp,
        settings.rangeType,
        settings.referrer,
        settings.schemeColumnCount,
        settings.passwordExpiryLastShownAt,
        generation,
        setLanguage,
        setLayoutDirection,
        setSchemeState,
        setTCILevels,
        setTheme,
        setTab,
        setStationId,
        setTrainId,
        setSelectedItem,
        setStartTimestamp,
        setEndTimestamp,
        setTimestamps,
        setRangeType,
        setReferrer,
        setSchemeColumnCount,
        setPasswordExpiryLastShownAt,
        resetSettings,
    ]);
    return isSynchronized ? ((0, jsx_runtime_1.jsx)(context_1.SettingsContext.Provider, { value: contextValue, children: (0, jsx_runtime_1.jsx)(react_1.Fragment, { children: children }, `${storageKey}:${generation}`) })) : null;
};
exports.SettingsProvider = SettingsProvider;
//# sourceMappingURL=provider.js.map